import { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { api, type Geoportal, type Table, type Layer } from '../lib/api';
import { LayerSettings } from '../components/LayerSettings';
import { DrillConfigPanel } from '../components/DrillConfigPanel';
import { cleanPaintForMapLibre } from '../lib/paint';
import { BASE_MAPS, baseMapSource, safeBaseMapId, type BaseMapId } from '../lib/basemap';

export function GeoportalEditor() {
  const { id } = useParams();
  const [gp, setGp]             = useState<Geoportal | null>(null);
  const [tables, setTables]     = useState<Table[]>([]);
  const [layers, setLayers]     = useState<Layer[]>([]);
  const [selected, setSelected] = useState<Layer | null>(null);
  const [baseMap, setBaseMap]   = useState<BaseMapId>('osm-positron');
  const mapDiv = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);

  // ───────── load all ─────────
  useEffect(() => {
    if (!id) return;
    api.getGeoportal(id).then(g => { setGp(g); setBaseMap(safeBaseMapId(g.baseMap)); });
    api.listTables().then(r => setTables(r.tables));
    api.listLayers(id).then(r => setLayers(r.items));
  }, [id]);

  // ───────── init map ─────────
  useEffect(() => {
    if (!gp || !mapDiv.current || mapRef.current) return;
    const sources: Record<string, maplibregl.SourceSpecification> = {};
    const styleLayers: maplibregl.LayerSpecification[] = [];
    const baseSrc = baseMapSource(safeBaseMapId(gp.baseMap));
    if (baseSrc) {
      sources['base'] = baseSrc;
      styleLayers.push({ id: 'base', type: 'raster', source: 'base' });
    }
    mapRef.current = new maplibregl.Map({
      container: mapDiv.current,
      style: { version: 8, sources, layers: styleLayers },
      center: gp.defaultView.center,
      zoom: gp.defaultView.zoom,
    });
  }, [gp]);

  // ───────── sync layers → map (re-render every change) ─────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (!map.isStyleLoaded()) {
      map.once('load', () => syncLayers());
      return;
    }
    syncLayers();
    function syncLayers() {
      if (!map) return;
      // Quitamos sólo nuestras sources/layers (l_*) — dejamos `base` intacta.
      const style = map.getStyle();
      for (const layerSpec of (style.layers ?? []).slice().reverse()) {
        if (layerSpec.id.startsWith('l_') && map.getLayer(layerSpec.id)) {
          map.removeLayer(layerSpec.id);
        }
      }
      for (const sid of Object.keys(style.sources ?? {})) {
        if (sid.startsWith('l_') && map.getSource(sid)) map.removeSource(sid);
      }
      for (const l of layers) {
        const sourceId = `l_${l.id}`;
        // Cache buster con updatedAt — cada save de la capa fuerza tiles frescas.
        const v = new Date(l.updatedAt ?? Date.now()).getTime();
        map.addSource(sourceId, {
          type: 'vector',
          tiles: [`${location.origin}/tiles/${l.id}/{z}/{x}/{y}.mvt?v=${v}`],
          minzoom: l.minZoom, maxzoom: l.maxZoom,
        });
        const baseType = l.geometryType.includes('Polygon') ? 'fill' : l.geometryType.includes('Line') ? 'line' : 'circle';
        const visibility = l.visibleDefault ? 'visible' : 'none';
        map.addLayer({
          id: sourceId,
          source: sourceId,
          'source-layer': 'mvt',
          type: baseType as any,
          paint: cleanPaintForMapLibre(l.paint as any) as any,
          layout: { visibility },
        });
        if (baseType === 'fill') {
          const outline = (l.paint as any)['fill-outline-color'];
          if (outline) {
            map.addLayer({
              id: sourceId + '_outline',
              source: sourceId,
              'source-layer': 'mvt',
              type: 'line',
              paint: { 'line-color': outline, 'line-width': 1 },
              layout: { visibility },
            });
          }
        }
      }
    }
  }, [layers]);

  async function refreshLayers() {
    if (!id) return;
    const r = await api.listLayers(id);
    setLayers(r.items);
    if (selected) {
      const fresh = r.items.find(x => x.id === selected.id);
      setSelected(fresh ?? null);
    }
  }

  async function addLayer(t: Table) {
    if (!id) return;
    const T = (t.geometry_type ?? '').toUpperCase();
    if (T === 'GEOMETRY' || T === '') {
      alert(`No se pudo determinar el tipo de geometría de ${t.table}. Subí la tabla con un tipo específico (MultiPolygon/Polygon/Line/Point).`);
      return;
    }
    const isMulti = T.includes('MULTI');
    const gt: Layer['geometryType'] =
      T.includes('POLYGON') ? (isMulti ? 'MultiPolygon' : 'Polygon') :
      T.includes('LINE')    ? (isMulti ? 'MultiLine'    : 'Line')    :
                              (isMulti ? 'MultiPoint'   : 'Point');
    const paint =
      gt.includes('Polygon') ? { __mode: 'solid', 'fill-color': '#0ea5e9', 'fill-opacity': 0.4, 'fill-outline-color': '#0c4a6e' } :
      gt.includes('Line')    ? { __mode: 'solid', 'line-color': '#0ea5e9', 'line-width': 2, 'line-opacity': 1 } :
                               { __mode: 'solid', 'circle-color': '#0ea5e9', 'circle-radius': 5, 'circle-stroke-color': '#0c4a6e', 'circle-stroke-width': 1 };
    const created = await api.createLayer({
      geoportalId: id,
      name: t.table,
      sourceSchema: t.schema,
      sourceTable: t.table,
      geomColumn: t.geom_column,
      geomSrid: t.srid,
      geometryType: gt,
      attributes: [],
      paint,
      ord: layers.length,
    });
    await refreshLayers();
    setSelected(created);
  }

  async function setCurrentViewAsDefault() {
    if (!id || !mapRef.current) return;
    const center = mapRef.current.getCenter();
    const view = {
      center: [center.lng, center.lat] as [number, number],
      zoom: mapRef.current.getZoom(),
      bearing: mapRef.current.getBearing(),
      pitch: mapRef.current.getPitch(),
    };
    await api.updateGeoportal(id, { defaultView: view });
    setGp(g => g ? { ...g, defaultView: view } : g);
  }

  async function changeBaseMap(b: BaseMapId) {
    if (!id) return;
    setBaseMap(b);
    const map = mapRef.current;
    if (map) {
      if (map.getLayer('base')) map.removeLayer('base');
      if (map.getSource('base')) map.removeSource('base');
      const src = baseMapSource(b);
      if (src) {
        map.addSource('base', src);
        const firstCustom = layers[0] ? `l_${layers[0].id}` : undefined;
        map.addLayer({ id: 'base', type: 'raster', source: 'base' }, firstCustom);
      }
    }
    await api.updateGeoportal(id, { baseMap: b });
    setGp(g => g ? { ...g, baseMap: b } : g);
  }

  function toggleVisibility(l: Layer) {
    const map = mapRef.current; if (!map) return;
    const next = !l.visibleDefault;
    setLayers(ls => ls.map(x => x.id === l.id ? { ...x, visibleDefault: next } : x));
    const vis = next ? 'visible' : 'none';
    if (map.getLayer(`l_${l.id}`))         map.setLayoutProperty(`l_${l.id}`, 'visibility', vis);
    if (map.getLayer(`l_${l.id}_outline`)) map.setLayoutProperty(`l_${l.id}_outline`, 'visibility', vis);
    api.updateLayer(l.id, { visibleDefault: next }).catch(() => {});
  }

  async function moveLayer(idx: number, dir: -1 | 1) {
    const j = idx + dir;
    if (j < 0 || j >= layers.length) return;
    const a = layers[idx], b = layers[j];
    await api.updateLayer(a.id, { ord: b.ord });
    await api.updateLayer(b.id, { ord: a.ord });
    await refreshLayers();
  }

  if (!gp) return <div style={{ padding: 24 }}>Cargando…</div>;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr 360px', height: '100%' }}>
      {/* ── Sidebar izquierdo ── */}
      <aside style={{ borderRight: '1px solid #e2e8f0', overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
        <header style={{ padding: '12px 16px', borderBottom: '1px solid #e2e8f0' }}>
          <Link to="/" style={{ fontSize: 12, color: '#64748b', textDecoration: 'none' }}>← Mis geoportales</Link>
          <h2 style={{ margin: '6px 0 2px', fontSize: 16 }}>{gp.name}</h2>
          <code style={{ fontSize: 12, color: '#64748b' }}>/{gp.slug}</code>
          <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <a href={`/v/${gp.slug}`} target="_blank" style={{ fontSize: 12, color: '#0ea5e9' }}>Ver público →</a>
            <button onClick={setCurrentViewAsDefault} style={btnSm}>📍 Vista actual como default</button>
          </div>
          <div style={{ marginTop: 8 }}>
            <label style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 4 }}>Mapa base</label>
            <select value={baseMap} onChange={e => changeBaseMap(e.target.value as BaseMapId)}
              style={{ width: '100%', fontSize: 12, padding: '4px 6px', border: '1px solid #cbd5e1', borderRadius: 4, background: 'white' }}>
              {BASE_MAPS.map(b => <option key={b.id} value={b.id}>{b.label}</option>)}
            </select>
          </div>
          {id && (
            <DrillConfigPanel
              geoportalId={id}
              layers={layers}
              initial={gp.drillConfig}
              onSaved={() => api.getGeoportal(id).then(setGp)}
            />
          )}
        </header>

        <section style={{ padding: '12px 16px' }}>
          <h3 style={{ fontSize: 12, textTransform: 'uppercase', color: '#64748b', margin: 0, marginBottom: 8 }}>Capas ({layers.length})</h3>
          {layers.length === 0 && <p style={{ fontSize: 13, color: '#94a3b8' }}>Aún sin capas. Agregá una desde abajo.</p>}
          {layers.map((l, idx) => (
            <div key={l.id}
              onClick={() => setSelected(l)}
              style={{
                padding: '8px 10px', borderRadius: 6, cursor: 'pointer',
                background: selected?.id === l.id ? '#0ea5e91a' : 'transparent',
                border: `1px solid ${selected?.id === l.id ? '#0ea5e9' : '#e2e8f0'}`,
                marginBottom: 4, fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6,
              }}>
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                <strong>{l.name}</strong>
                <div style={{ color: '#64748b', fontSize: 11 }}>{l.geometryType}</div>
              </span>
              <span style={{ display: 'flex', gap: 4 }}>
                <button onClick={e => { e.stopPropagation(); toggleVisibility(l); }} title={l.visibleDefault ? 'Ocultar' : 'Mostrar'}
                  style={{ ...btnIcon, background: l.visibleDefault ? '#dbeafe' : 'white' }}>
                  {l.visibleDefault ? '👁' : '✕'}
                </button>
                <button onClick={e => { e.stopPropagation(); moveLayer(idx, -1); }} disabled={idx === 0} style={btnIcon}>↑</button>
                <button onClick={e => { e.stopPropagation(); moveLayer(idx, +1); }} disabled={idx === layers.length - 1} style={btnIcon}>↓</button>
              </span>
            </div>
          ))}
        </section>

        <section style={{ padding: '12px 16px', borderTop: '1px solid #e2e8f0' }}>
          <h3 style={{ fontSize: 12, textTransform: 'uppercase', color: '#64748b', margin: 0, marginBottom: 8 }}>Tablas disponibles ({tables.length})</h3>
          {tables.map(t => (
            <div key={`${t.schema}.${t.table}`} style={{ padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 4, marginBottom: 4, fontSize: 13, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>
                <strong>{t.table}</strong>
                <div style={{ color: '#94a3b8', fontSize: 11 }}>{t.geometry_type} · srid {t.srid}</div>
              </span>
              <button style={btnAdd} onClick={() => addLayer(t)}>+</button>
            </div>
          ))}
        </section>
      </aside>

      {/* ── Mapa central ── */}
      <div ref={mapDiv} />

      {/* ── Panel derecho ── */}
      <aside style={{ borderLeft: '1px solid #e2e8f0', overflowY: 'auto', padding: 16 }}>
        {selected
          ? <LayerSettings key={selected.id} layer={selected} onChange={refreshLayers} onDelete={async () => { await api.deleteLayer(selected.id); setSelected(null); refreshLayers(); }} />
          : <p style={{ color: '#94a3b8', fontSize: 13 }}>Seleccioná una capa para editar paint, popup, atributos.</p>}
      </aside>
    </div>
  );
}

const btnIcon: React.CSSProperties = { width: 22, height: 22, padding: 0, border: '1px solid #cbd5e1', background: 'white', borderRadius: 4, fontSize: 11, cursor: 'pointer' };
const btnAdd:  React.CSSProperties = { width: 24, height: 24, padding: 0, border: 'none', background: '#0ea5e9', color: 'white', borderRadius: 4, fontSize: 14, cursor: 'pointer', fontWeight: 600 };
const btnSm:   React.CSSProperties = { fontSize: 11, padding: '3px 6px', border: '1px solid #cbd5e1', background: 'white', borderRadius: 4, cursor: 'pointer' };
